import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import React, { useEffect, useState } from 'react';
import DueTimeModeTypeOption, { DueTimeModeType } from '@/types/DueTimeType';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import { RouteMapExecutionService } from '@services/actions/RouteMapExecutionService';
import { getABookFormValues } from '@utils/abookUtils';
import TransitionTypeOption from '@/types/TransitionType';
import {
    IExtraStepSettingsResult,
    IRouteMapActionResult,
} from '@models/document/routeMapActionsExecutors/IRouteMapActionModel';
import DurationSpanTypeOption from '@/types/DurationSpanType';
import { useForm } from 'react-hook-form';
import { IOption } from '@srm/ui/types/IOption';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import { useActionSubmit } from '@organisms/actions/useActionSubmit';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { IAddRouteMapProps } from './IAddRouteMapProps';
import { IAddRouteMapFormData } from './IAddRouteMapFormData';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import AbookpickerControl from '@controls/AbookpickerControl';
import SelectControl from '@srm/ui/controls/SelectControl';
import InputNumberControl from '@srm/ui/controls/InputNumberControl';
import DateFieldControl from '@srm/ui/controls/DateFieldControl';
import { useDocumentAbookTabs } from '@/hooks/useDocumentAbookTabs';

export const AddRouteMapModal: React.FC<IAddRouteMapProps> = (props: IAddRouteMapProps) => {
    const abookTabs = useDocumentAbookTabs(props.documentKey, props.abookOptions?.tabs);
    const [periodType, setPeriodType] = useState<DueTimeModeType>(DueTimeModeType[props.defaultDueTime.type]);
    const [executors, setExecutors] = useState<IAddressBookData[]>();
    const [formValues, setFormValues] = useState<string>('{}');
    const activated = React.useRef(false);

    const [restrictedTransitionType, setRestrictedTransitionType] = useState<boolean>();
    const [defaultTransitionType, setDefaultTransitionType] = useState<IOption[]>([TransitionTypeOption[0]]);

    const serv = new RouteMapExecutionService();

    const { errorText, setErrorText, isBusy, setIsBusy, isSubmitting, okClick, cancelClick } = useActionSubmit({
        completeHandler: props.completeHandler,
        initialBusy: true,
        submit: (result: IRouteMapActionResult) =>
            serv.add(props.actionKey, props.activityKey, props.processKey, props.documentKey, result),
    });

    const getFormValues = async () => {
        const vals = await getABookFormValues(props.documentKey, props.abookOptions?.formValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        activated.current = true;
        setIsBusy(true);
        serv.getExtraStepSettings(props.processKey)
            .then((res) => {
                if (ignore) return;
                void getFormValues();
                setStageSettings(res.data);
            })
            .catch(() => {
                if (!ignore) setErrorText('Ошибка получения данных от сервера.');
            })
            .finally(() => {
                if (!ignore) setIsBusy(false);
            });
        return () => {
            ignore = true;
            activated.current = false;
        };
    }, []);

    const setStageSettings = (data: IExtraStepSettingsResult) => {
        if (
            data.restrictedTransitionType != undefined &&
            data.restrictedTransitionType &&
            data.restrictedStepNames != undefined &&
            data.restrictedStepNames.length > 0
        ) {
            const currentStep = props.rowData?.fields['Name'];
            if (currentStep != undefined && data.restrictedStepNames.indexOf(currentStep) > -1) {
                setRestrictedTransitionType(true);
                setDefaultTransitionType([TransitionTypeOption[1]]);
                formState.reset({ ...formState.getValues(), transitionType: [TransitionTypeOption[1]] });
            }
        }
    };

    const onSubmit = (form: IAddRouteMapFormData) => {
        const transitionType = (form.transitionType[0] ?? TransitionTypeOption[0]).value;

        const result = {
            levelKey: props.levelKey,
            nextlevelKey: props.nextlevelKey,
            stepName: form.stepName,
            executorsIds: form.executors.split('|').map((n) => parseInt(n, 10)),
            dueTimeMode: DueTimeModeType[periodType],
            transitionType: transitionType,
            tasktext: form.tasktext,
        } as IRouteMapActionResult;

        if (periodType == DueTimeModeType.Date) {
            result.periodDate = form.periodDate;
        }

        if (periodType == DueTimeModeType.TimeSpan) {
            result.termType = (form.termType[0] ?? DurationSpanTypeOption[0]).value;
            result.termCount = form.termCount ?? 1;
        }

        return okClick(result);
    };

    const formState = useForm<IAddRouteMapFormData>({
        mode: 'onBlur', // "onChange"
    });
    const hasUnsavedInput = useUnsavedFormInput(formState, { textFields: ['tasktext'] });

    const onPeriodTypeChange = (option: IOption[]) => {
        if (option.length == 0) return;
        const type = DueTimeModeType[option[0].value as keyof typeof DueTimeModeType];
        setPeriodType(type);
    };

    const periodDateIsRequired = periodType === DueTimeModeType.Date;

    const modalContainerProps: IModalContainerProps = {
        header: 'Создание этапа',
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        closeDisabled: isSubmitting,
        hasUnsavedInput,
        onCancel: cancelClick,
        errorText: errorText,
        formMethods: formState,
        onSubmit: onSubmit,
        okButtonDisabled: false,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <TextareaControl
                label="Название этапа"
                initialRowCount={1}
                readOnly={true}
                required={true}
                defaultValue={'Дополнительное согласование'}
                rules={{ required: REQUIRED_MESSAGES.text }}
                name="stepName"
                control={formState.control}
            />

            <AbookpickerControl
                label="Исполнители"
                placeholder="Выберите исполнителей"
                isMultiple={true}
                title="Исполнители"
                displayValue={executors?.map((x) => x.fullName)?.join(',') ?? ''}
                formValues={formValues}
                name="executors"
                tabsSettings={abookTabs}
                externalSearch={props.abookOptions?.externalSearch}
                required={true}
                rules={{ required: REQUIRED_MESSAGES.multiple }}
                showChips={false}
                control={formState.control}
                onSetValues={(value: IAddressBookData[]) => {
                    setExecutors(value);
                }}
            />

            <SelectControl
                name="periodType"
                label="Срок исполнения"
                multiselect={false}
                readOnly
                placeholder="Укажите срок исполнения"
                required={true}
                rules={{ required: REQUIRED_MESSAGES.select }}
                options={DueTimeModeTypeOption}
                loading={isBusy}
                control={formState.control}
                defaultValue={[DueTimeModeTypeOption.find((option) => option.value === props.defaultDueTime.type)]}
                onValueChange={onPeriodTypeChange}
            />

            {periodType === DueTimeModeType.TimeSpan && (
                <>
                    <InputNumberControl
                        name="termCount"
                        defaultValue={props.defaultDueTime.timeSpanValue ?? 1}
                        min={1}
                        max={9999}
                        floatPoints={0}
                        required={true}
                        rules={{ required: REQUIRED_MESSAGES.number }}
                        control={formState.control}
                    />

                    <SelectControl
                        multiselect={false}
                        name="termType"
                        readOnly
                        required={true}
                        rules={{ required: REQUIRED_MESSAGES.select }}
                        options={DurationSpanTypeOption}
                        loading={isBusy}
                        control={formState.control}
                        defaultValue={[
                            DurationSpanTypeOption.find(
                                (option) => option.value === props.defaultDueTime.timeSpanType,
                            ) ?? DurationSpanTypeOption[2],
                        ]}
                    />
                </>
            )}

            {periodType === DueTimeModeType.Date && (
                <DateFieldControl
                    name="periodDate"
                    required={periodDateIsRequired}
                    rules={periodDateIsRequired ? { required: REQUIRED_MESSAGES.date } : undefined}
                    control={formState.control}
                />
            )}

            <SelectControl
                label="Тип этапа"
                multiselect={false}
                readOnly
                required={true}
                rules={{ required: REQUIRED_MESSAGES.select }}
                name="transitionType"
                // если этап добавляется в пустую карту маршрута, то доступен только тип этапа Последующий
                options={
                    restrictedTransitionType
                        ? [TransitionTypeOption[1]]
                        : props.activityKey
                          ? TransitionTypeOption
                          : [TransitionTypeOption[0]]
                }
                loading={isBusy}
                control={formState.control}
                defaultValue={defaultTransitionType}
            />

            <TextareaControl
                label="Текст задачи"
                readOnly={false}
                required={true}
                rules={requiredTextRules()}
                name="tasktext"
                defaultValue={props.prefilledComment}
                control={formState.control}
            />
        </ModalContainer>
    );
};
